import { type FieldError, type Control, type FieldPath, type FieldValues, useController } from 'react-hook-form';
import Textarea, { type TextareaProps as BaseTextareaProps } from '@repo/textarea';

export interface ControlledTextareaProps<TFieldValues extends FieldValues = FieldValues> extends Omit<BaseTextareaProps, 'error' | 'onChange' | 'onBlur' | 'name' | 'ref' | 'value' | 'defaultValue'> {
    name: FieldPath<TFieldValues>;
    control: Control<TFieldValues>;
    error?: FieldError;
    className?: string;
}

export default function ControlledTextarea<TFieldValues extends FieldValues = FieldValues>({
    name,
    control,
    error: errorProp,
    className = '',
    ...props
}: ControlledTextareaProps<TFieldValues>) {
    const { field, fieldState } = useController({
        control,
        name,
    });

    const error = fieldState.error || errorProp;

    const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        field.onChange(event.target.value);
    };

    const handleBlur = () => {
        field.onBlur();
    };

    return (
        <Textarea
            className={className}
            error={error?.message}
            value={field.value ?? ''}
            onChange={handleChange}
            onBlur={handleBlur}
            {...props}
        />
    );
}
